import { useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';

import { SelectableOption } from '@/common-components/elements/SelectableOption/SelectableOption';

import type { FeedbackOption } from '../types';

interface FeedbackViewProps {
  rating: 'positive' | 'negative' | null;
  positiveOptions: FeedbackOption[];
  negativeOptions: FeedbackOption[];
  selectedFeedbackOptions: string[];
  additionalComments: string;
  onToggleOption: (value: string) => void;
  onCommentsChange: (comments: string) => void;
  onSubmit: () => void;
}

export const FeedbackView = ({
  rating,
  positiveOptions,
  negativeOptions,
  selectedFeedbackOptions,
  additionalComments,
  onToggleOption,
  onCommentsChange,
  onSubmit,
}: FeedbackViewProps) => {
  // state
  const currentOptions = useMemo(() => {
    return rating === 'positive' ? positiveOptions : negativeOptions;
  }, [rating, positiveOptions, negativeOptions]);

  // variables
  const feedbackOptions = useMemo(
    () => (
      <div className="vai-flex vai-flex-wrap vai-gap-3">
        {currentOptions.map((option) => (
          <SelectableOption
            key={option.value}
            value={option.value}
            isSelected={selectedFeedbackOptions.includes(option.value)}
            onToggle={onToggleOption}
          >
            {option.label}
          </SelectableOption>
        ))}
      </div>
    ),
    [currentOptions, selectedFeedbackOptions, onToggleOption],
  );

  return (
    <div data-testid="feedback-view">
      <div className="vai-space-y-4">
        {/* Feedback Options */}
        <div className="vai-space-y-3">{feedbackOptions}</div>
        {/* Additional Comments */}
        <div className="vai-space-y-2">
          <label
            htmlFor="feedback-comments"
            className="vai-text-sm vai-font-medium vai-text-gray-700"
          >
            <Trans>Share your feedback with the team</Trans>
          </label>
          <textarea
            id="feedback-comments"
            value={additionalComments}
            onChange={(e) => onCommentsChange(e.target.value)}
            className="vai-w-full vai-h-24 vai-p-3 vai-border vai-border-gray-300 vai-rounded-md vai-resize-none vai-focus:vai-outline-none vai-focus:vai-ring-2 vai-focus:vai-ring-blue-500 vai-focus:vai-border-transparent"
            placeholder=""
            data-testid="feedback-comments"
          />
        </div>
      </div>
      <div className="vai-flex vai-justify-end vai-pt-0">
        {/* Submit feedback button */}
        <Button
          onClick={onSubmit}
          disabled={selectedFeedbackOptions.length === 0}
          data-testid="dialog-feedback-submit-button"
        >
          <Trans>Submit feedback</Trans>
        </Button>
      </div>
    </div>
  );
};
